import { useState, useEffect } from 'react'
import getQuickScheduleData from '../utils/getQuickScheduleData'

const useQuickSchedule = (driverCode, stopType = null) => {
    const [status, setStatus] = useState('loading') // loading | ready | error | no-shipment | no-appointment-required
    const [errorMessage, setErrorMessage] = useState(null)
    const [data, setData] = useState(null)
    const [adminDate, setAdminDate] = useState(null)
    const [pickupOrDelivery, setPickupOrDelivery] = useState(null)

    // Single effect — fires only when driverCode or adminDate changes.
    // Avoids the cascading effect / double-call pattern in useCarrier.js.
    useEffect(() => {
        if (!driverCode) return
        loadData()
    }, [driverCode, adminDate]) // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        setStatus('loading')

        const params = new URLSearchParams()
        if (adminDate) params.append('adminDate', adminDate)
        if (stopType) params.append('stopType', stopType)

        // Pass through adhoc mode params from URL
        const urlParams = new URLSearchParams(window.location.search)
        const mode = urlParams.get('mode')
        if (mode) params.append('mode', mode)

        const queryString = params.toString() ? '?' + params.toString() : ''
        const cv = await getQuickScheduleData(driverCode, queryString)

        if (!cv) {
            setStatus('error')
            setErrorMessage('Failed to load shipment data. Please try reloading.')
            return
        }
        if (cv.status === 'error') {
            setStatus('error')
            setErrorMessage(cv.errorStatus || 'An error occurred loading this shipment.')
            return
        }
        if (!cv.shipment) {
            setStatus('no-shipment')
            return
        }

        const apptRequired = cv.appointmentRequired || {}
        const urlAdHocType = urlParams.get('type')

        if (!apptRequired.pickup && !apptRequired.delivery && !urlAdHocType) {
            setStatus('no-appointment-required')
            return
        }

        const existingPickupAppts = (cv.existingAppts || []).filter(e => e.type === 'Pickup')
        const existingDeliveryAppts = (cv.existingAppts || []).filter(e => e.type === 'Delivery')

        setData({
            shipment: cv.shipment,
            existingPickupAppts,
            existingDeliveryAppts,
            pickupRequired: apptRequired.pickup || false,
            deliveryRequired: apptRequired.delivery || false,
            shipperLocationInfo: cv.shipperLocationInfo,
            consigneeLocationInfo: cv.consigneeLocationInfo,
            consolidatedOpenTimeWindows: cv.consolidatedOpenTimeWindows || { pickup: [], delivery: [] },
            openTimeWindows: cv.openTimeWindows || {},
        })

        // Determine which tab to show first
        if (apptRequired.pickup) {
            setPickupOrDelivery('Pickup')
        } else if (apptRequired.delivery) {
            setPickupOrDelivery('Delivery')
        } else if (urlAdHocType === 'pickup') {
            setPickupOrDelivery('Pickup')
        } else if (urlAdHocType === 'delivery') {
            setPickupOrDelivery('Delivery')
        }

        setStatus('ready')
    }

    return {
        status,
        errorMessage,
        data,
        adminDate,
        setAdminDate,
        pickupOrDelivery,
        setPickupOrDelivery,
        reload: loadData
    }
}

export default useQuickSchedule
